import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  BigQueryFieldSchema,
  BigQueryRepeatedValue,
  BigQueryRequest,
  BigQueryResponse,
  BigQueryRow,
  BigQueryRowValue,
  BigQueryTableMetadata,
} from './bigquery.model';

@Injectable({ providedIn: 'root' })
export class BigQueryHttpService {
  constructor(private readonly http: HttpClient) {}

  private readonly baseUrl = 'https://bigquery.googleapis.com/bigquery/v2';
  private readonly defaultProjectId = 'examdojo-analytics';
  private readonly defaultDataset = '00_dbt_05_business_intelligence';

  query(query: string, projectId: string = this.defaultProjectId, useLegacySql = false): Observable<BigQueryResponse> {
    const url = `${this.baseUrl}/projects/${projectId}/queries`;

    const request: BigQueryRequest = {
      query,
      useLegacySql,
    };

    return this.http.post<BigQueryResponse>(url, request);
  }

  queryAnalytics(query: string): Observable<BigQueryResponse> {
    return this.query(query, this.defaultProjectId);
  }

  /**
   * Queries BigQuery and transforms the response into typed objects.
   * Returns raw objects based on the schema - domain-specific mapping should be done by the caller.
   */
  queryAndTransform<T extends Record<string, unknown>>(query: string): Observable<T[]> {
    return this.queryAnalytics(query).pipe(map((response) => this.transformResponse<T>(response)));
  }

  /**
   * Transforms a BigQuery response into an array of typed objects.
   */
  transformResponse<T extends Record<string, unknown>>(response: BigQueryResponse): T[] {
    if (!response.rows || !response.schema) {
      return [];
    }

    return response.rows.map((row: BigQueryRow) => {
      const rowData: Record<string, unknown> = {};

      response.schema.fields.forEach((field: BigQueryFieldSchema, index: number) => {
        const cellValue = row.f[index]?.v;
        rowData[field.name] = this.transformField(cellValue, field);
      });

      return rowData as T;
    });
  }

  fetchTableMetadata(tableName: string, dataset: string = this.defaultDataset): Observable<BigQueryTableMetadata> {
    const metadataQuery = `
      SELECT 
        creation_time as lastModified
      FROM \`${this.defaultProjectId}.${dataset}.INFORMATION_SCHEMA.TABLES\`
      WHERE table_name = '${tableName}'
    `;

    return this.queryAnalytics(metadataQuery).pipe(
      map((response) => {
        if (!response.rows || response.rows.length === 0) {
          return { lastModified: new Date() };
        }

        const row = response.rows[0];
        const rawTimestamp = row.f[0]?.v;

        // Handle different BigQuery timestamp formats
        let lastModified: Date;

        if (typeof rawTimestamp === 'object' && rawTimestamp !== null && 'value' in rawTimestamp) {
          // Object format: { value: "2025-10-01T19:54:36.318Z" }
          const isoString = (rawTimestamp as { value: string }).value;
          lastModified = new Date(isoString);
        } else if (typeof rawTimestamp === 'string') {
          // Check if it's a Unix timestamp in scientific notation (e.g., "1.7593484763318E9")
          const numericTimestamp = parseFloat(rawTimestamp);
          if (!isNaN(numericTimestamp)) {
            // Convert seconds to milliseconds for Date constructor
            lastModified = new Date(numericTimestamp * 1000);
          } else {
            // Try parsing as ISO string
            lastModified = new Date(rawTimestamp);
          }
        } else {
          lastModified = new Date();
        }

        // If invalid, use current date
        if (isNaN(lastModified.getTime())) {
          return { lastModified: new Date() };
        }

        return { lastModified };
      }),
      catchError((error: unknown) => {
        console.error('Error fetching table metadata:', error);
        return of({ lastModified: new Date() });
      }),
    );
  }

  /**
   * Transforms a single BigQuery field value based on its schema type.
   */
  private transformField(value: unknown, field: BigQueryFieldSchema): unknown {
    if (value === null || value === undefined) {
      return field.mode === 'REPEATED' ? [] : null;
    }

    // Handle REPEATED fields (arrays)
    if (field.mode === 'REPEATED') {
      if (!Array.isArray(value)) {
        return [];
      }
      return value.map((item: BigQueryRepeatedValue) => item?.v || item);
    }

    // Handle RECORD fields (nested objects)
    if (field.type === 'RECORD' && field.fields && value && typeof value === 'object' && 'f' in value) {
      const rowValue = value as BigQueryRowValue;
      const recordData: Record<string, unknown> = {};
      field.fields.forEach((subField: BigQueryFieldSchema, subIndex: number) => {
        const subValue = rowValue.f[subIndex]?.v;
        recordData[subField.name] = this.transformField(subValue, subField);
      });
      return recordData;
    }

    // Handle JSON strings
    if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    }

    // Convert numeric field types from strings to numbers
    if (field.type === 'INTEGER' || field.type === 'FLOAT' || field.type === 'NUMERIC') {
      if (typeof value === 'string') {
        const numValue = Number(value);
        return isNaN(numValue) ? 0 : numValue;
      }
      return Number(value);
    }

    return value;
  }
}
